import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import type { ItineraryConflict } from '@/utils/itineraryScheduling';
import { View, Pressable, ScrollView, Platform, InteractionManager } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { groupItineraryItemsByDate } from '@/services/itinerary';
import { detectConflicts } from '@/utils/itineraryScheduling';
import { fromLocalDateKey, toLocalDateKey } from '@/utils/dateKeys';
import { getDateKeyForTripDay } from '@/utils/tripCalendar';
import { getTripDayContext } from '@/utils/tripDayContext';
import { resolveItemEndInstant, resolveItemStartInstant, resolveTripSurfaceTimezone } from '@/utils/tripItemSchedule';
import { ItineraryItemCompactCard } from './ItineraryItemCompactCard';
import { ItineraryItemDetailsSheet } from './ItineraryItemDetailsSheet';
import { FillYourDay } from './FillYourDay';
import type { ItineraryItem } from '@/services/itinerary';
import type { Booking } from '@/services/bookings';
import type { Trip, TripDestination } from '@/services/trips';
import type { WeatherAlert } from './DayWeatherSheet';
import { getCachedTravelTime } from '@/utils/travelTimeCache';
import { estimateTravelTime } from '@/utils/itineraryScheduling';
import { toIso2CountryCode } from '@/utils/countryIso2';

type CalendarItineraryViewProps = {
  items: ItineraryItem[];
  bookings?: Booking[];
  startDate: Date;
  endDate: Date;
  /** One-shot deep link / widget day focus. Must not keep syncing after the user picks a day. */
  focusedDateKey?: string;
  preferredTravelMode?: 'walking' | 'driving' | 'transit';
  onEditItem?: (item: ItineraryItem) => void;
  onDeleteItem?: (item: ItineraryItem) => void;
  onViewDocument?: (url: string, meta?: { offlinePath?: string | null; fileName?: string; contentType?: string | null }) => void;
  canEdit?: boolean;
  onAddItem?: (date?: Date) => void;
  onOpenIdeas?: (preferredTab?: 'ai' | 'localPicks' | 'amadeus' | 'for_you', targetDate?: Date) => void;
  tripDestination?: string;
  tripDestinations?: TripDestination[]; // Multi-city destinations
  dailyWeather?: Record<
    string,
    {
      iconName: string;
      label: string;
      tempMin?: number;
      tempMax?: number;
      precipProb?: number;
      windSpeed?: number;
      condition?: string;
      description?: string;
      source?: 'forecast' | 'historical';
    }
  >;
  dailyTimezone?: Record<string, { label: string; timezone?: string }>;
  dailyAlerts?: Record<string, WeatherAlert[]>;
  onOpenDayWeather?: (dateKey: string) => void;
  onResolveConflict?: (conflictItem: ItineraryItem) => void;
  trip?: Trip; // Trip object for FillYourDay
};

const DAYS_OF_WEEK_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const withAlpha = (color: string, alphaHex: string) => {
  if (typeof color === 'string' && color.startsWith('#') && color.length === 7) return `${color}${alphaHex}`;
  return color;
};

const formatTime = (time?: string): string | null => {
  if (!time || time === 'flexible') return null;
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  if (Number.isNaN(hour)) return null;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes || '00'} ${ampm}`;
};

const formatDuration = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes <= 0) return '';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const withTimeOnDate = (date: Date, time: string): Date | null => {
  if (!time || time === 'flexible') return null;
  const [h, m] = time.split(':').map((v) => parseInt(v, 10));
  if (!Number.isFinite(h)) return null;
  const dt = new Date(date);
  dt.setHours(h, Number.isFinite(m) ? m : 0, 0, 0);
  return dt;
};

const getEndDateTime = (item: ItineraryItem): Date | null => {
  if (!item.startTime || item.startTime === 'flexible') return null;
  const start = withTimeOnDate(item.startDate, item.startTime);
  if (!start) return null;
  if (item.endTime) {
    const end = withTimeOnDate(item.startDate, item.endTime);
    if (end) return end;
  }
  // Fallback to 2 hours for “no end time”
  const end = new Date(start);
  end.setHours(end.getHours() + 2);
  return end;
};

// Generate all dates in the trip range
const generateTripDates = (startDate: Date, endDate: Date): Date[] => {
  const dates: Date[] = [];
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  
  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
};

// Format date for display: "11/28 Sat" or "12/1 Tues"
const formatDateShort = (date: Date): string => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const dayOfWeek = DAYS_OF_WEEK_SHORT[date.getDay()];
  return `${month}/${day} ${dayOfWeek}`;
};

export function CalendarItineraryView({
  items,
  bookings = [],
  startDate,
  endDate,
  focusedDateKey,
  preferredTravelMode,
  onEditItem,
  onDeleteItem,
  onViewDocument,
  canEdit = false,
  onAddItem,
  onOpenIdeas,
  tripDestination,
  tripDestinations,
  dailyWeather,
  dailyTimezone,
  dailyAlerts,
  onOpenDayWeather,
  onResolveConflict,
  trip,
}: CalendarItineraryViewProps) {
  const theme = useTheme();
  const tripDayContext = useMemo(() => (trip ? getTripDayContext(trip) : null), [trip]);
  const tripTimeZone = tripDayContext?.timeZone ?? resolveTripSurfaceTimezone(trip ?? ({} as Trip));
  const activeTodayKey = useMemo(() => {
    if (trip && tripDayContext?.state === 'active' && tripDayContext.currentDay) {
      return getDateKeyForTripDay(trip, tripDayContext.currentDay);
    }
    return toLocalDateKey(new Date());
  }, [trip, tripDayContext?.currentDay, tripDayContext?.state, tripTimeZone]);
  const todayKey = activeTodayKey;
  const today = useMemo(() => fromLocalDateKey(todayKey), [todayKey]);
  const startDateKey = trip && tripDayContext
    ? getDateKeyForTripDay(trip, 1)
    : toLocalDateKey(startDate);
  const endDateKey = trip && tripDayContext
    ? getDateKeyForTripDay(trip, tripDayContext.totalDays)
    : toLocalDateKey(endDate);
  const rangeKey = `${startDateKey}:${endDateKey}`;
  const autoSelectedForRangeRef = useRef<string | null>(null);
  const userPickedDateRef = useRef(false);
  const appliedRouteFocusRef = useRef<string | null>(null);
  
  const tripDates = useMemo(() => {
    if (trip && tripDayContext) {
      return Array.from({ length: tripDayContext.totalDays }, (_, index) =>
        fromLocalDateKey(getDateKeyForTripDay(trip, index + 1)),
      );
    }
    return generateTripDates(startDate, endDate);
  }, [trip, tripDayContext, startDate, endDate, tripTimeZone]);
  const groupedByDate = useMemo(
    () => groupItineraryItemsByDate(items, trip ? { trip } : undefined),
    [items, trip],
  );
  const bookingLookup = useMemo(() => {
    const map = new Map<string, Booking>();
    bookings?.forEach((booking) => {
      map.set(booking.id, booking);
    });
    return map;
  }, [bookings]);

  // Helper function to determine which city a date belongs to
  const getCityForDate = (date: Date): string | undefined => {
    if (!tripDestinations || tripDestinations.length <= 1) return undefined;
    
    const dest = tripDestinations.find(dest => {
      const destStart = new Date(dest.startDate);
      const destEnd = new Date(dest.endDate);
      destStart.setHours(0, 0, 0, 0);
      destEnd.setHours(23, 59, 59, 999);
      return date >= destStart && date <= destEnd;
    });
    
    return dest?.city;
  };
  
  // Initialize selected date to start date or today if in range
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    if (todayKey >= startDateKey && todayKey <= endDateKey) {
      return today;
    }
    return startDate;
  });
  
  const dateScrollRef = useRef<ScrollView>(null);
  const dateLayouts = useRef<Record<string, { x: number; width: number }>>({});
  const [scrollViewWidth, setScrollViewWidth] = useState(0);
  
  // Scroll to selected date when it changes
  const selectedIndex = tripDates.findIndex(
    (d) => toLocalDateKey(d) === toLocalDateKey(selectedDate)
  );

  useEffect(() => {
    if (tripDates.length === 0) return;
    dateLayouts.current = {};
  }, [tripDates.length]);

  useEffect(() => {
    if (selectedIndex < 0 || scrollViewWidth === 0) return;

    const layout = dateLayouts.current[toLocalDateKey(selectedDate)];
    if (layout && dateScrollRef.current) {
      const target =
        Math.max(0, layout.x + layout.width / 2 - scrollViewWidth / 2);
      dateScrollRef.current.scrollTo({ x: target, animated: true });
      return;
    }

    if (dateScrollRef.current) {
      const itemWidth = 96;
      const target = Math.max(0, selectedIndex * itemWidth - scrollViewWidth / 2);
      dateScrollRef.current.scrollTo({ x: target, animated: true });
    }
  }, [selectedDate, selectedIndex, scrollViewWidth, tripDates.length]);
  
  const handleDateSelect = (date: Date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
    userPickedDateRef.current = true;
    autoSelectedForRangeRef.current = rangeKey;
    setSelectedDate(date);
  };

  useEffect(() => {
    if (!focusedDateKey) return;
    if (userPickedDateRef.current) return;
    if (focusedDateKey < startDateKey || focusedDateKey > endDateKey) return;

    const focusToken = `${rangeKey}:${focusedDateKey}`;
    if (appliedRouteFocusRef.current === focusToken) return;
    appliedRouteFocusRef.current = focusToken;
    autoSelectedForRangeRef.current = rangeKey;

    const target = tripDates.find((date) => toLocalDateKey(date) === focusedDateKey);
    setSelectedDate(target ?? fromLocalDateKey(focusedDateKey));
  }, [endDateKey, focusedDateKey, rangeKey, startDateKey, tripDates]);
  
  const selectedDateKey = toLocalDateKey(selectedDate);
  const selectedDateItems = groupedByDate.get(selectedDateKey) || [];
  const selectedDayTimezone = dailyTimezone?.[selectedDateKey]?.timezone;
  const selectedWeather = dailyWeather?.[selectedDateKey];
  const selectedTimezone = dailyTimezone?.[selectedDateKey];

  const [showFillYourDay, setShowFillYourDay] = useState(false);
  useEffect(() => {
    setShowFillYourDay(false);
  }, [selectedDateKey]);

  useEffect(() => {
    if (autoSelectedForRangeRef.current !== rangeKey) {
      autoSelectedForRangeRef.current = null;
      userPickedDateRef.current = false;
    }

    const isSelectedInRange =
      selectedDateKey >= startDateKey && selectedDateKey <= endDateKey;
    if (isSelectedInRange) return;

    const fallback =
      todayKey >= startDateKey && todayKey <= endDateKey ? today : startDate;
    const fallbackKey = toLocalDateKey(fallback);
    if (fallbackKey !== selectedDateKey) {
      setSelectedDate(fallback);
    }
  }, [
    endDateKey,
    rangeKey,
    selectedDateKey,
    startDate,
    startDateKey,
    today,
    todayKey,
  ]);

  useEffect(() => {
    if (autoSelectedForRangeRef.current === rangeKey) return;
    if (userPickedDateRef.current) {
      autoSelectedForRangeRef.current = rangeKey;
      return;
    }
    if (items.length === 0) return;
    if ((groupedByDate.get(selectedDateKey) || []).length > 0) {
      autoSelectedForRangeRef.current = rangeKey;
      return;
    }

    const firstTripDateWithItems = tripDates.find(
      (date) => (groupedByDate.get(toLocalDateKey(date)) || []).length > 0,
    );
    if (!firstTripDateWithItems) return;

    autoSelectedForRangeRef.current = rangeKey;
    setSelectedDate(firstTripDateWithItems);
  }, [groupedByDate, items.length, rangeKey, selectedDateKey, tripDates]);
  
  // Detect conflicts for all items (async) - made lazy to prevent blocking
  const [allConflicts, setAllConflicts] = useState<ItineraryConflict[]>([]);
  const [conflictsLoading, setConflictsLoading] = useState(false);
  
  // Memoize items array to prevent unnecessary re-runs
  const itemsKey = useMemo(() => {
    // Include fields that affect conflict detection; otherwise conflicts can appear "stuck" after edits.
    return items
      .map(item => {
        const dateKey = toLocalDateKey(item.startDate);
        const updated = item.updatedAt instanceof Date ? item.updatedAt.getTime() : new Date(item.updatedAt as any).getTime();
        return `${item.id}:${dateKey}:${item.startTime || ''}:${item.endTime || ''}:${updated || ''}`;
      })
      .join('|');
  }, [items]);

  useEffect(() => {
    // Skip if no items to avoid unnecessary API calls
    if (!items || items.length === 0) {
      setAllConflicts([]);
      setConflictsLoading(false);
      return;
    }
    
    // Skip conflict detection if too many items (performance optimization)
    if (items.length > 50) {
      console.warn('[CalendarItineraryView] Too many items, skipping conflict detection');
      setAllConflicts([]);
      setConflictsLoading(false);
      return;
    }
    
    let cancelled = false;
    setConflictsLoading(true);
    
    // Defer conflict detection significantly to prevent blocking render
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      
      // Add timeout to prevent hanging
      const detectionTimeout = setTimeout(() => {
        if (!cancelled) {
          console.warn('[CalendarItineraryView] detectConflicts taking too long, cancelling');
          cancelled = true;
          setConflictsLoading(false);
          setAllConflicts([]);
        }
      }, 5000); // 5 second timeout (reduced from 8)
      
      detectConflicts(items, preferredTravelMode || 'driving')
        .then(conflicts => {
          clearTimeout(detectionTimeout);
          if (!cancelled) {
            setAllConflicts(conflicts);
            setConflictsLoading(false);
          }
        })
        .catch(error => {
          clearTimeout(detectionTimeout);
          console.error('[CalendarItineraryView] Error detecting conflicts:', error);
          if (!cancelled) {
            setAllConflicts([]); // Set empty array on error to prevent blocking
            setConflictsLoading(false);
          }
        });
    }, 500); // Defer by 500ms to allow render to complete first
    
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      setConflictsLoading(false);
    };
  }, [itemsKey, preferredTravelMode]); // Use itemsKey instead of items array
  
  // Create a map of item IDs to their conflicts
  const itemConflictsMap = useMemo(() => {
    const map = new Map<string, ItineraryConflict[]>();
    allConflicts.forEach(conflict => {
      if (!map.has(conflict.item1.id)) {
        map.set(conflict.item1.id, []);
      }
      if (!map.has(conflict.item2.id)) {
        map.set(conflict.item2.id, []);
      }
      map.get(conflict.item1.id)!.push(conflict);
      map.get(conflict.item2.id)!.push(conflict);
    });
    return map;
  }, [allConflicts]);
  
  // Sort items by time (earliest to latest). Memoized so opening/closing details
  // doesn’t rebuild the whole list.
  const sortedItems = useMemo(() => {
    // Items with "flexible" time go at the end
    return [...selectedDateItems].sort((a, b) => {
      const aIsFlexible = !a.startTime || a.startTime === 'flexible';
      const bIsFlexible = !b.startTime || b.startTime === 'flexible';
      if (aIsFlexible && bIsFlexible) return 0;
      if (aIsFlexible) return 1;
      if (bIsFlexible) return -1;
      return (a.startTime || '').localeCompare(b.startTime || '');
    });
  }, [selectedDateItems]);

  // Details sheet state
  const [selectedItemForDetails, setSelectedItemForDetails] = useState<ItineraryItem | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);

  const openDetails = useCallback((item: ItineraryItem) => {
    setSelectedItemForDetails(item);
    setDetailsVisible(true);
  }, []);

  const closeDetails = useCallback(() => {
    setDetailsVisible(false);
  }, []);

  // Compute “Now / Next” for today, used for subtle emphasis.
  const now = useMemo(() => new Date(), [selectedDateKey]); // refresh on date switch
  const isSelectedToday = selectedDateKey === activeTodayKey;
  const { nowId, nextId } = useMemo(() => {
    if (!isSelectedToday || sortedItems.length === 0 || !trip) {
      return { nowId: null as string | null, nextId: null as string | null };
    }
    const cursor = new Date();
    for (const it of sortedItems) {
      if (!it.startTime || it.startTime === 'flexible') continue;
      const start = resolveItemStartInstant(it, tripTimeZone, trip);
      const end = resolveItemEndInstant(it, tripTimeZone, trip);
      if (!start || !end) continue;
      if (cursor >= start && cursor <= end) return { nowId: it.id, nextId: null };
    }
    for (const it of sortedItems) {
      if (!it.startTime || it.startTime === 'flexible') continue;
      const start = resolveItemStartInstant(it, tripTimeZone, trip);
      if (start && start > cursor) return { nowId: null, nextId: it.id };
    }
    return { nowId: null, nextId: null };
  }, [isSelectedToday, sortedItems, now, trip, tripTimeZone]);

  // Gap travel-time map for the selected day only.
  const [gapTravelTimes, setGapTravelTimes] = useState<Record<string, number | null>>({});
  const [gapEstimating, setGapEstimating] = useState<Record<string, boolean>>({});
  const gapTravelCacheRef = useRef<Record<string, Record<string, number | null>>>({});
  const sortedItemsSignature = useMemo(
    () =>
      sortedItems
        .map((item) => `${item.id}:${item.startTime || ''}:${item.endTime || ''}:${item.location || item.address || ''}`)
        .join('|'),
    [sortedItems],
  );

  const getItemCountryIso2 = useCallback(
    (item: ItineraryItem): string | undefined => {
      if (!tripDestinations || tripDestinations.length === 0) return undefined;
      const idx = typeof item.cityIndex === 'number' ? item.cityIndex : null;
      const d = (idx != null && tripDestinations[idx]) ? tripDestinations[idx] : tripDestinations[0];
      const raw = (d as any)?.countryCode || (d as any)?.country;
      if (typeof raw !== 'string' || !raw.trim()) return undefined;
      const iso2 = toIso2CountryCode(raw);
      return iso2 || (raw.trim().length === 2 ? raw.trim().toUpperCase() : undefined);
    },
    [tripDestinations],
  );

  useEffect(() => {
    let cancelled = false;
    const cacheKey = `${selectedDateKey}:${preferredTravelMode || 'driving'}:${sortedItemsSignature}`;
    const cached = gapTravelCacheRef.current[cacheKey];
    if (cached) {
      setGapTravelTimes(cached);
    } else {
      setGapTravelTimes({});
    }

    const compute = async () => {
      const pairs: Array<{ key: string; from: ItineraryItem; to: ItineraryItem; departAt: Date }> = [];
      for (let i = 0; i < sortedItems.length - 1; i++) {
        const a = sortedItems[i];
        const b = sortedItems[i + 1];
        if (!a.endTime || !b.startTime || b.startTime === 'flexible') continue;
        const end = withTimeOnDate(a.startDate, a.endTime);
        if (!end) continue;
        const fromLoc = a.location || a.address;
        const toLoc = b.location || b.address;
        if (!fromLoc || !toLoc) continue;
        pairs.push({ key: `${a.id}->${b.id}`, from: a, to: b, departAt: end });
      }

      // Cache-only lookup first (fast, avoids geocode/route API calls on initial render)
      const mode = (preferredTravelMode || 'driving') as any;
      const toEstimate: Array<{ key: string; fromLoc: string; toLoc: string; departAt: Date; country?: string }> = [];
      
      const entries = await Promise.all(
        pairs.map(async (p) => {
          const fromLoc = (p.from.location || p.from.address)!;
          const toLoc = (p.to.location || p.to.address)!;
          const country = getItemCountryIso2(p.to) || getItemCountryIso2(p.from);
          const cached = await getCachedTravelTime(fromLoc, toLoc, mode, p.departAt, country);
          
          // Queue uncached items for estimation (limit to first 5)
          if (cached === null && toEstimate.length < 5) {
            toEstimate.push({ key: p.key, fromLoc, toLoc, departAt: p.departAt, country });
          }
          
          return [p.key, cached] as const;
        }),
      );

      if (!cancelled) {
        const nextMap: Record<string, number | null> = {};
        entries.forEach(([k, v]) => {
          nextMap[k] = v;
        });
        setGapTravelTimes(nextMap);
        gapTravelCacheRef.current[cacheKey] = nextMap;
      }
      
      // Auto-estimate uncached travel times in background (limit concurrent calls)
      if (!cancelled && toEstimate.length > 0) {
        const defaultTimes: Record<string, number> = { walking: 20, driving: 25, transit: 30 };
        const estimatedUpdates: Record<string, number> = {};
        
        for (const { key, fromLoc, toLoc, departAt, country } of toEstimate) {
          if (cancelled) break;
          try {
            const mins = await estimateTravelTime(fromLoc, toLoc, {
              mode,
              departureTime: departAt,
              defaultTime: defaultTimes[mode] || 25,
              country,
            });
            if (!cancelled) {
              estimatedUpdates[key] = mins;
            }
          } catch (error) {
            if (__DEV__) {
              console.warn('[CalendarItineraryView] Gap travel time estimate failed', error);
            }
          }
        }
        if (!cancelled && Object.keys(estimatedUpdates).length > 0) {
          setGapTravelTimes((prev) => {
            const merged = { ...prev, ...estimatedUpdates };
            gapTravelCacheRef.current[cacheKey] = merged;
            return merged;
          });
        }
      }
    };

    // Defer heavy travel-time work until interactions settle, so day navigation stays smooth.
    const interactionTask = InteractionManager.runAfterInteractions(() => {
      if (!cancelled) {
        void compute();
      }
    });
    return () => {
      cancelled = true;
      interactionTask.cancel?.();
    };
  }, [sortedItems, sortedItemsSignature, preferredTravelMode, selectedDateKey, getItemCountryIso2]);

  const handleEstimateGapTravel = useCallback(
    async (from: ItineraryItem, to: ItineraryItem, departAt: Date, key: string) => {
      if (gapEstimating[key]) return;
      setGapEstimating((prev) => ({ ...prev, [key]: true }));
      // Let the UI paint the loading state before doing any heavier work.
      await new Promise((r) => setTimeout(r, 0));
      try {
        const mode = preferredTravelMode || 'driving';
        // Use mode-aware default times
        const defaultTimes = { walking: 20, driving: 25, transit: 30 };
        const country = getItemCountryIso2(to) || getItemCountryIso2(from);
        const mins = await estimateTravelTime(from.location || from.address, to.location || to.address, {
          mode,
          departureTime: departAt,
          defaultTime: defaultTimes[mode] || 25,
          country,
        });
        setGapTravelTimes((prev) => ({ ...prev, [key]: mins }));
      } finally {
        setGapEstimating((prev) => ({ ...prev, [key]: false }));
      }
    },
    [gapEstimating, preferredTravelMode, getItemCountryIso2],
  );

  const timelineList = useMemo(() => {
    if (sortedItems.length === 0) return null;
    const railDot = theme.colors.textSecondary;
    const railLine = withAlpha(theme.colors.textSecondary, '70');
    return (
      <View style={{ gap: theme.spacing(1.5) }}>
        {sortedItems.map((item, idx) => {
          const isLast = idx === sortedItems.length - 1;
          const startLabel = item.startTime ? formatTime(item.startTime) : null;
          const status = item.id === nowId ? 'Now' : item.id === nextId ? 'Next' : null;
          const statusColor =
            status === 'Now' ? theme.colors.primary : status === 'Next' ? theme.colors.textSecondary : theme.colors.textSecondary;

          const conflicts = itemConflictsMap.get(item.id) || [];
          const conflictSeverity = conflicts.some((c) => c.severity === 'error')
            ? 'error'
            : conflicts.some((c) => c.severity === 'warning')
              ? 'warning'
              : null;
          const primaryConflict =
            conflicts.find((c) => c.severity === 'error') ||
            conflicts.find((c) => c.severity === 'warning') ||
            conflicts[0] ||
            null;
          const conflictMessage = primaryConflict?.message || null;

          const next = sortedItems[idx + 1];
          const canRenderGap = !!item.endTime && !!next?.startTime && next.startTime !== 'flexible';
          const gapStart = canRenderGap ? withTimeOnDate(item.startDate, item.endTime!) : null;
          const gapEnd = canRenderGap ? withTimeOnDate(next.startDate, next.startTime!) : null;
          const gapMinutes = gapStart && gapEnd ? Math.round((gapEnd.getTime() - gapStart.getTime()) / (1000 * 60)) : null;
          const gapKey = next ? `${item.id}->${next.id}` : null;
          const travelMinutes = gapKey ? (gapTravelTimes[gapKey] ?? null) : null;
          const isEstimating = gapKey ? Boolean(gapEstimating[gapKey]) : false;
          const showGap = !!gapMinutes && gapMinutes > 0 && gapStart && gapEnd;
          const bufferMinutes = showGap && typeof travelMinutes === 'number' ? gapMinutes - travelMinutes : null;

          return (
            <View key={item.id} style={{ gap: theme.spacing(1.5) }}>
              <View style={{ flexDirection: 'row', alignItems: 'stretch', gap: theme.spacing(1.25) }}>
                {/* Time rail */}
                <View style={{ width: 62, paddingTop: 4 }}>
                  <ThemedText variant="caption" style={{ fontSize: 12, fontWeight: '700' }}>
                    {startLabel || '—'}
                  </ThemedText>
                  {!!status && (
                    <Caption style={{ marginTop: 2, color: statusColor, fontWeight: '700' }}>
                      {status}
                    </Caption>
                  )}
                </View>

                <View style={{ width: 16, alignItems: 'center' }}>
                  <View
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: item.id === nowId ? theme.colors.primary : railDot,
                      marginTop: 8,
                    }}
                  />
                  <View
                    style={{
                      flex: 1,
                      width: 2,
                      backgroundColor: isLast ? 'transparent' : railLine,
                      marginTop: 6,
                    }}
                  />
                </View>

                <View style={{ flex: 1 }}>
                  <ItineraryItemCompactCard
                    item={item}
                    tripDestinations={tripDestinations}
                    contextDateKey={selectedDateKey}
                    conflictSeverity={conflictSeverity as any}
                    conflictMessage={conflictMessage}
                    conflictActionLabel={conflictSeverity ? 'Fix' : undefined}
                    onConflictActionPress={
                      conflictSeverity && onResolveConflict
                        ? () => {
                            const first = conflicts[0];
                            const other = first ? (first.item1.id === item.id ? first.item2 : first.item1) : null;
                            if (other) onResolveConflict(other);
                          }
                        : conflictSeverity && onEditItem
                          ? () => onEditItem(item)
                          : undefined
                    }
                    onPress={() => openDetails(item)}
                    showTimeRange={true}
                    showSecondaryLine={true}
                  />
                </View>
              </View>

              {/* Gap block */}
              {showGap && next && (
                <View style={{ flexDirection: 'row', alignItems: 'stretch', gap: theme.spacing(1.25) }}>
                  <View style={{ width: 62 }} />
                  <View style={{ width: 16, alignItems: 'center' }}>
                    <View
                      style={{
                        flex: 1,
                        width: 2,
                        backgroundColor: withAlpha(theme.colors.textSecondary, '40'),
                      }}
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <View
                      style={{
                        padding: theme.spacing(1.25),
                        borderRadius: theme.radii.lg,
                        backgroundColor: theme.colors.borderMuted + '18',
                        borderWidth: 1,
                        borderColor: theme.colors.borderMuted + '35',
                        gap: 4,
                      }}
                    >
                      <ThemedText variant="bodyStrong" style={{ fontSize: 13, fontWeight: '700' }}>
                        {formatTime(item.endTime!) || '—'}–{formatTime(next.startTime!) || '—'} • {formatDuration(gapMinutes)}
                      </ThemedText>
                      {typeof travelMinutes === 'number' && (
                        <Caption style={{ color: theme.colors.textSecondary }}>
                          Travel: {formatDuration(travelMinutes)} to {next.title}
                          {typeof bufferMinutes === 'number' && bufferMinutes > 0 ? ` • Buffer: ${formatDuration(bufferMinutes)}` : ''}
                        </Caption>
                      )}
                      {travelMinutes === null && (
                        <Pressable
                          onPress={() => {
                            if (!gapKey || !gapStart) return;
                            const fromLoc = item.location || item.address;
                            const toLoc = next.location || next.address;
                            if (!fromLoc || !toLoc) return;
                            void handleEstimateGapTravel(item, next, gapStart, gapKey);
                          }}
                          disabled={isEstimating || !gapKey || !gapStart}
                          style={({ pressed }) => ({
                            opacity: pressed ? 0.7 : 1,
                            alignSelf: 'flex-start',
                          })}
                          accessibilityRole="button"
                          accessibilityLabel={isEstimating ? 'Estimating travel time' : 'Estimate travel time'}
                          accessibilityHint="Calculates travel time between these activities"
                        >
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                            <Ionicons
                              name={isEstimating ? 'time-outline' : 'sparkles-outline'}
                              size={14}
                              color={theme.colors.textSecondary}
                              accessible={false}
                            />
                            <Caption style={{ color: theme.colors.textSecondary }}>
                              {isEstimating ? 'Estimating travel…' : `Travel: — to ${next.title} • Tap to estimate`}
                            </Caption>
                          </View>
                        </Pressable>
                      )}
                    </View>
                  </View>
                </View>
              )}
            </View>
          );
        })}
      </View>
    );
  }, [
    gapEstimating,
    gapTravelTimes,
    handleEstimateGapTravel,
    itemConflictsMap,
    nextId,
    nowId,
    openDetails,
    sortedItems,
    theme,
  ]);

  return (
    <View style={{ flex: 1 }}>
      {/* Horizontal Date Selector - Sticky Header */}
      <View
        accessibilityLabel="Select itinerary date"
        style={{
          backgroundColor: theme.colors.surface,
          paddingVertical: theme.spacing(1),
          paddingBottom: theme.spacing(1.5),
          position: 'relative',
          overflow: 'visible',
          borderBottomWidth: 1,
          borderBottomColor: theme.colors.borderMuted,
          marginBottom: theme.spacing(1),
          marginHorizontal: -theme.spacing(2),
          paddingHorizontal: theme.spacing(0.5),
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {/* Left Arrow */}
          <Pressable
            onPress={() => {
              const previousDate = selectedIndex > 0 ? tripDates[selectedIndex - 1] : null;
              if (previousDate) {
                handleDateSelect(previousDate);
              }
            }}
            disabled={selectedIndex <= 0}
            style={({ pressed }) => [
              {
                minWidth: 44,
                minHeight: 44,
                padding: theme.spacing(1),
                alignItems: 'center',
                justifyContent: 'center',
                opacity: pressed ? 0.6 : 1,
              },
            ]}
            accessibilityRole="button"
            accessibilityLabel="Previous day"
            accessibilityHint="Select the previous date in your trip"
            accessibilityState={{ disabled: selectedIndex <= 0 }}
            hitSlop={8}
          >
            <Ionicons
              name="chevron-back"
              size={20}
              color={selectedIndex <= 0 ? theme.colors.textSecondary : theme.colors.text}
              accessible={false}
            />
          </Pressable>

          <ScrollView
            ref={dateScrollRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            onLayout={({ nativeEvent }) => setScrollViewWidth(nativeEvent.layout.width)}
            contentContainerStyle={{
              paddingHorizontal: theme.spacing(1),
              flexGrow: 1,
            }}
            style={{ flex: 1 }}
          >
            {tripDates.map((date) => {
              const dateKey = toLocalDateKey(date);
              const isSelected = dateKey === selectedDateKey;
              const dayItemsCount = (groupedByDate.get(dateKey) || []).length;
              const hasItems = dayItemsCount > 0;
              const isToday = dateKey === toLocalDateKey(today);
              const weather = dailyWeather?.[dateKey];
              const tz = dailyTimezone?.[dateKey];
              const alerts = dailyAlerts?.[dateKey] || [];
              const topAlert = alerts[0];
              const alertColor =
                topAlert?.severity === 'critical'
                  ? theme.colors.error
                  : topAlert?.severity === 'high'
                    ? theme.colors.warning
                    : topAlert?.severity === 'medium'
                      ? theme.colors.primary
                      : theme.colors.textSecondary;
              const alertSeverityLabel =
                topAlert?.severity === 'critical'
                  ? 'Critical'
                  : topAlert?.severity === 'high'
                    ? 'High'
                    : topAlert?.severity === 'medium'
                      ? 'Moderate'
                      : null;

              return (
                <Pressable
                  key={dateKey}
                  onPress={() => handleDateSelect(date)}
                  onLongPress={() => {
                    if (weather && onOpenDayWeather) onOpenDayWeather(dateKey);
                  }}
                  delayLongPress={250}
                  onLayout={({ nativeEvent }) => {
                    dateLayouts.current[dateKey] = {
                      x: nativeEvent.layout.x,
                      width: nativeEvent.layout.width,
                    };
                  }}
                  style={({ pressed }) => [
                    {
                      paddingHorizontal: theme.spacing(2),
                      paddingVertical: theme.spacing(1),
                      borderRadius: theme.radii.lg,
                      backgroundColor: isSelected ? 'transparent' : 'transparent',
                      minHeight: 44,
                      minWidth: 70,
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: pressed ? 0.7 : 1,
                      borderWidth: isSelected ? 1.5 : 0,
                      borderColor: isSelected ? theme.colors.primary : 'transparent',
                      borderStyle: 'solid',
                      marginRight: theme.spacing(1),
                    },
                  ]}
                  accessibilityRole={Platform.OS === 'android' ? 'button' : 'tab'}
                  accessibilityLabel={`${formatDateShort(date)}${isToday ? ', today' : ''}${hasItems ? `, ${dayItemsCount} ${dayItemsCount === 1 ? 'item' : 'items'}` : ', no items'}${isSelected ? ', selected' : ''}`}
                  accessibilityHint={weather && onOpenDayWeather ? "Select this date to view itinerary items. Long press to view weather details." : "Select this date to view itinerary items"}
                  accessibilityState={{ selected: isSelected }}
                  hitSlop={6}
                >
                  <ThemedText
                    variant="bodyStrong"
                    style={{
                      fontSize: 13,
                      color: isSelected ? theme.colors.primary : theme.colors.text,
                      fontWeight: isSelected ? '600' : '400',
                    }}
                  >
                    {formatDateShort(date)}
                  </ThemedText>
                  {weather && (
                    <View style={{ marginTop: theme.spacing(0.25), flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <Ionicons
                        name={weather.iconName as any}
                        size={14}
                        color={isSelected ? theme.colors.primary : theme.colors.textSecondary}
                      />
                      <ThemedText
                        variant="caption"
                        style={{ fontSize: 11, color: isSelected ? theme.colors.primary : theme.colors.textSecondary }}
                        numberOfLines={1}
                      >
                        {weather.label}
                      </ThemedText>
                      {!!topAlert && (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                          <Ionicons
                            name="alert-circle-outline"
                            size={14}
                            color={isSelected ? theme.colors.primary : alertColor}
                            accessibilityLabel={`${alertSeverityLabel || 'Weather'} alert`}
                          />
                          {!!alertSeverityLabel && (
                            <Caption
                              style={{
                                fontSize: 10,
                                fontWeight: '700',
                                color: isSelected ? theme.colors.primary : alertColor,
                              }}
                            >
                              {alertSeverityLabel}
                            </Caption>
                          )}
                        </View>
                      )}
                    </View>
                  )}
                  {!weather && tz && (
                    <View style={{ marginTop: theme.spacing(0.25), flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <ThemedText
                        variant="caption"
                        style={{ fontSize: 11, color: isSelected ? theme.colors.primary : theme.colors.textSecondary }}
                        numberOfLines={1}
                      >
                        {tz.label}
                      </ThemedText>
                      {!!topAlert && (
                        <Ionicons
                          name="alert-circle-outline"
                          size={14}
                          color={isSelected ? theme.colors.primary : alertColor}
                          accessibilityLabel="Weather alert"
                        />
                      )}
                    </View>
                  )}
                  {weather && tz && (
                    <ThemedText
                      variant="caption"
                      style={{ marginTop: theme.spacing(0.15), fontSize: 10, color: isSelected ? theme.colors.primary : theme.colors.textSecondary }}
                      numberOfLines={1}
                    >
                      {tz.label}
                    </ThemedText>
                  )}
                  {hasItems && !isSelected && (
                    <View
                      accessible={false}
                      style={{
                        marginTop: theme.spacing(0.25),
                        width: 4,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: theme.colors.primary,
                      }}
                    />
                  )}
                </Pressable>
              );
            })}
          </ScrollView>

          {/* Right Arrow */}
          <Pressable
            onPress={() => {
              const nextDate = selectedIndex < tripDates.length - 1 ? tripDates[selectedIndex + 1] : null;
              if (nextDate) {
                handleDateSelect(nextDate);
              }
            }}
            disabled={selectedIndex >= tripDates.length - 1}
            style={({ pressed }) => [
              {
                minWidth: 44,
                minHeight: 44,
                padding: theme.spacing(1),
                alignItems: 'center',
                justifyContent: 'center',
                opacity: pressed ? 0.6 : 1,
              },
            ]}
            accessibilityRole="button"
            accessibilityLabel="Next day"
            accessibilityHint="Select the next date in your trip"
            accessibilityState={{ disabled: selectedIndex >= tripDates.length - 1 }}
            hitSlop={8}
          >
            <Ionicons
              name="chevron-forward"
              size={20}
              color={
                selectedIndex >= tripDates.length - 1 ? theme.colors.textSecondary : theme.colors.text
              }
              accessible={false}
            />
          </Pressable>
        </View>
      </View>

      {/* Items List for Selected Date */}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: theme.spacing(2),
          gap: theme.spacing(2),
        }}
      >
        {/* Itinerary Items */}
        {sortedItems.length === 0 ? (
          <View style={{ paddingVertical: theme.spacing(2), gap: theme.spacing(2) }}>
            <View style={{ alignItems: 'center', paddingVertical: theme.spacing(3), gap: theme.spacing(1.25) }}>
              <ThemedText variant="bodyStrong" style={{ fontSize: 16 }}>
                No items for this day
              </ThemedText>
              <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
                Add activities, flights, or stays to this day to keep everything in sync.
              </Body>

              {(selectedWeather || selectedTimezone) && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75), marginTop: theme.spacing(0.5) }}>
                  {selectedWeather && (
                    <>
                      <Ionicons name={selectedWeather.iconName as any} size={16} color={theme.colors.textSecondary} />
                      <ThemedText variant="caption" style={{ color: theme.colors.textSecondary }}>
                        {selectedWeather.label}
                      </ThemedText>
                    </>
                  )}
                  {selectedTimezone && (
                    <ThemedText variant="caption" style={{ color: theme.colors.textSecondary }}>
                      {selectedWeather ? ` • ${selectedTimezone.label}` : selectedTimezone.label}
                    </ThemedText>
                  )}
                </View>
              )}

              {canEdit && onAddItem && (
                <View style={{ flexDirection: 'row', gap: theme.spacing(1), marginTop: theme.spacing(1), width: '100%' }}>
                  <Button
                    label="Add item"
                    variant="primary"
                    onPress={() => onAddItem(selectedDate)}
                    icon={<Ionicons name="add-outline" size={18} color={theme.colors.onPrimary} />}
                    style={{ flex: 1 }}
                  />
                  {onOpenIdeas && (
                    <Button
                      label="Add from ideas"
                      variant="secondary"
                      onPress={() => onOpenIdeas('localPicks', selectedDate)}
                      icon={<Ionicons name="bulb-outline" size={18} color={theme.colors.text} />}
                      style={{ flex: 1 }}
                    />
                  )}
                </View>
              )}

              {canEdit && trip && (
                <Button
                  label={showFillYourDay ? 'Hide suggestions' : 'Fill this day'}
                  variant="secondary"
                  onPress={() => setShowFillYourDay((v) => !v)}
                  icon={<Ionicons name="sparkles-outline" size={18} color={theme.colors.primary} />}
                />
              )}
            </View>

            {canEdit && trip && showFillYourDay && (
              <View style={{ height: 420 }}>
                <FillYourDay
                  trip={trip}
                  day={selectedDate}
                  existingItems={items}
                  onItemAdded={() => {
                    // Items will refresh automatically via subscription
                  }}
                />
              </View>
            )}
          </View>
        ) : (
          timelineList
        )}

        {/* Fill Your Day (opt-in) */}
        {canEdit && trip && sortedItems.length > 0 && (
          <View style={{ marginTop: theme.spacing(2), gap: theme.spacing(1.25) }}>
            <Button
              label={showFillYourDay ? 'Hide suggestions' : 'Fill this day'}
              variant="secondary"
              onPress={() => setShowFillYourDay((v) => !v)}
              icon={<Ionicons name="sparkles-outline" size={18} color={theme.colors.primary} />}
            />
            {showFillYourDay && (
              <View style={{ height: 420 }}>
            <FillYourDay
              trip={trip}
              day={selectedDate}
              existingItems={items}
              onItemAdded={() => {
                // Items will refresh automatically via subscription
              }}
            />
              </View>
            )}
          </View>
        )}
        
        {/* Add Item Button at bottom of list (avoid duplicate CTA in empty state) */}
        {canEdit && onAddItem && sortedItems.length > 0 && (
          <View style={{ marginTop: theme.spacing(2), paddingTop: theme.spacing(2), borderTopWidth: 1, borderTopColor: theme.colors.borderMuted }}>
            <Button
              label="Add Item to This Day"
              variant="secondary"
              onPress={() => onAddItem(selectedDate)}
              icon={<Ionicons name="add-outline" size={18} color={theme.colors.primary} />}
            />
          </View>
        )}
      </ScrollView>

      <ItineraryItemDetailsSheet
        visible={detailsVisible}
        item={selectedItemForDetails}
        booking={selectedItemForDetails?.bookingId ? bookingLookup.get(selectedItemForDetails.bookingId) : undefined}
        allItems={items}
        preferredTravelMode={preferredTravelMode}
        onClose={closeDetails}
        onEditItem={onEditItem}
        onDeleteItem={onDeleteItem}
        onViewDocument={onViewDocument}
        canEdit={canEdit}
        tripStartDate={startDate}
        tripDestination={tripDestination}
        currentCity={getCityForDate(selectedDate)}
        dayTimezone={selectedDayTimezone}
        onResolveConflict={onResolveConflict}
        tripId={trip?.id}
      />
    </View>
  );
}
